import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CheckCircle2, AlertTriangle, Clock, Sparkles } from "lucide-react";
import { api } from "../api/client.js";
import { PageHeader, Panel, Spinner, StatTile } from "../components/Panels.js";
import { BU_ACCENT, SENTIMENT_COLOR, todayIsoDate } from "../lib/format.js";

interface DashboardStaff {
  staff_id: string;
  full_name: string;
  role: string;
  business_unit_code: string | null;
  business_unit_name: string | null;
  manager_name: string | null;
  check_in_status: string;
  responded_at: string | null;
  needs_attention: number | null;
  sentiment: string | null;
  llm_summary: string | null;
}

interface DashboardResponse {
  ok: true;
  date: string;
  stats: {
    total: number;
    responded: number;
    flagged: number;
    no_response: number;
    pending: number;
    response_rate: number;
    sentiment_score: number | null;
  };
  by_business_unit: Array<{
    code: string;
    name: string;
    total: number;
    responded: number;
    flagged: number;
    no_response: number;
    pending: number;
  }>;
  staff: DashboardStaff[];
  escalations: Array<{
    id: string;
    staff_name: string;
    business_unit: string | null;
    reason: string;
    severity: string;
    action_type: string;
    meeting_with: string | null;
    meeting_scheduled_for: string | null;
    status: string;
    description: string | null;
  }>;
  sentiment_trend: Array<Record<string, string | number>>;
  response_trend: Array<{ date: string; rate: number }>;
  activity: Array<{
    id: number;
    event_type: string;
    created_at: string;
    who: string | null;
    payload: unknown;
  }>;
}

export function DashboardPage() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [buFilter, setBuFilter] = useState<string>("all");
  const date = todayIsoDate();

  useEffect(() => {
    let cancelled = false;
    api
      .get<DashboardResponse>(`/dashboard?date=${date}`)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  const filteredStaff = useMemo(() => {
    if (!data) return [];
    if (buFilter === "all") return data.staff;
    return data.staff.filter((s) => s.business_unit_code === buFilter);
  }, [data, buFilter]);

  if (error) {
    return (
      <div className="p-6 text-sm text-sentiment-overwhelmed">
        Failed to load: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-6">
        <Spinner />
      </div>
    );
  }

  const s = data.stats;

  return (
    <>
      <PageHeader title="Today" subtitle={`Check-in status — ${data.date}`} />
      <div className="px-6 grid grid-cols-4 gap-4">
        <StatTile
          label="Response rate"
          value={`${s.response_rate}%`}
          hint={`${s.responded} of ${s.total} responded`}
        />
        <StatTile
          label="Flagged"
          value={s.flagged}
          hint="Triage rules fired"
        />
        <StatTile
          label="No response"
          value={s.no_response}
          hint={s.pending ? `${s.pending} still pending` : "All accounted for"}
        />
        <StatTile
          label="Sentiment"
          value={s.sentiment_score == null ? "—" : `${s.sentiment_score}`}
          hint="0 overwhelmed → 100 positive"
        />
      </div>

      <div className="px-6 mt-6 grid grid-cols-3 gap-4">
        <Panel title="Sentiment trend" className="col-span-2">
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={data.sentiment_trend}>
                <CartesianGrid stroke="#1d2230" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#8e95a8" fontSize={11} />
                <YAxis stroke="#8e95a8" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "#11161f",
                    border: "1px solid #1d2230",
                  }}
                />
                <Legend />
                {(["positive", "neutral", "concerned", "frustrated", "overwhelmed"] as const).map(
                  (k) => (
                    <Area
                      key={k}
                      type="monotone"
                      dataKey={k}
                      stackId="1"
                      stroke={SENTIMENT_COLOR[k]}
                      fill={SENTIMENT_COLOR[k]}
                      fillOpacity={0.35}
                    />
                  ),
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="By business unit">
          <div className="space-y-3">
            <BuRow
              code="all"
              name="All"
              total={s.total}
              active={buFilter === "all"}
              onClick={() => setBuFilter("all")}
            />
            {data.by_business_unit.map((bu) => (
              <BuRow
                key={bu.code}
                code={bu.code}
                name={bu.name}
                total={bu.total}
                responded={bu.responded}
                flagged={bu.flagged}
                active={buFilter === bu.code}
                onClick={() => setBuFilter(bu.code)}
              />
            ))}
          </div>
        </Panel>
      </div>

      <div className="px-6 mt-6 grid grid-cols-3 gap-4">
        <Panel title="Today's check-ins" className="col-span-2">
          <ul className="divide-y divide-bg-line">
            {filteredStaff.map((row) => (
              <li key={row.staff_id} className="py-3 flex items-start gap-3">
                <StatusDot status={row.check_in_status} flagged={row.needs_attention === 1} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-ink">{row.full_name}</span>
                    <span className="text-xs text-ink-dim">{row.role}</span>
                    {row.business_unit_name ? (
                      <span
                        className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border"
                        style={{
                          color: BU_ACCENT[row.business_unit_code ?? ""] ?? "#94a3b8",
                          borderColor: `${BU_ACCENT[row.business_unit_code ?? ""] ?? "#94a3b8"}55`,
                        }}
                      >
                        {row.business_unit_name}
                      </span>
                    ) : null}
                  </div>
                  {row.llm_summary ? (
                    <p className="mt-1 text-xs text-ink-dim line-clamp-2">{row.llm_summary}</p>
                  ) : (
                    <p className="mt-1 text-xs text-ink-dimmer italic">
                      {row.check_in_status === "pending" ? "Pending" : "—"}
                    </p>
                  )}
                </div>
                {row.sentiment ? (
                  <span
                    className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm"
                    style={{
                      color: SENTIMENT_COLOR[row.sentiment] ?? "#94a3b8",
                      background: "transparent",
                      border: `1px solid ${SENTIMENT_COLOR[row.sentiment] ?? "#94a3b8"}55`,
                    }}
                  >
                    {row.sentiment}
                  </span>
                ) : null}
              </li>
            ))}
            {filteredStaff.length === 0 ? (
              <li className="py-6 text-sm text-ink-dim italic">
                No staff in this filter.
              </li>
            ) : null}
          </ul>
        </Panel>

        <Panel title="Open escalations">
          <ul className="space-y-3">
            {data.escalations.map((e) => (
              <li
                key={e.id}
                className="rounded-md border border-bg-line p-3 text-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-ink">{e.staff_name}</span>
                  <span
                    className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm ${
                      e.severity === "urgent"
                        ? "bg-sentiment-overwhelmed/20 text-sentiment-overwhelmed"
                        : e.severity === "high"
                        ? "bg-sentiment-frustrated/20 text-sentiment-frustrated"
                        : "bg-sentiment-concerned/20 text-sentiment-concerned"
                    }`}
                  >
                    {e.severity}
                  </span>
                </div>
                <div className="text-xs text-ink-dim mt-1 line-clamp-2">
                  {e.description ?? e.reason}
                </div>
                {e.meeting_with ? (
                  <div className="text-[11px] text-ink-dimmer mt-2">
                    With {e.meeting_with}
                    {e.meeting_scheduled_for ? ` · ${e.meeting_scheduled_for}` : ""}
                  </div>
                ) : null}
              </li>
            ))}
            {data.escalations.length === 0 ? (
              <li className="text-sm text-ink-dim italic">No open escalations.</li>
            ) : null}
          </ul>
        </Panel>
      </div>
    </>
  );
}

function BuRow({
  name,
  total,
  responded,
  flagged,
  active,
  onClick,
  code,
}: {
  code: string;
  name: string;
  total: number;
  responded?: number;
  flagged?: number;
  active: boolean;
  onClick: () => void;
}) {
  const accent = BU_ACCENT[code] ?? "#94a3b8";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left flex items-center justify-between py-1.5 px-2 rounded-md text-sm ${
        active ? "bg-bg-surface text-ink" : "text-ink-dim hover:bg-bg-panel"
      }`}
    >
      <span className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: accent }} />
        {name}
      </span>
      <span className="font-mono text-xs">
        {responded == null ? total : `${responded}/${total}${flagged ? ` ⚑${flagged}` : ""}`}
      </span>
    </button>
  );
}

function StatusDot({ status, flagged }: { status: string; flagged: boolean }) {
  if (flagged)
    return <AlertTriangle size={14} className="mt-1 text-sentiment-frustrated" />;
  if (status === "responded")
    return <CheckCircle2 size={14} className="mt-1 text-sentiment-positive" />;
  if (status === "pending") return <Clock size={14} className="mt-1 text-ink-dim" />;
  if (status === "no_response")
    return <Sparkles size={14} className="mt-1 text-sentiment-concerned" />;
  return <Clock size={14} className="mt-1 text-ink-dim" />;
}
